import React, { useState, useEffect, useRef } from "react";
import { Typography, Space, Button } from "antd";
import { BellOutlined, RightOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { METRO_COLORS } from "../../../constants/colors";
import { useUser } from "../../../context/UserContext";
import newsService from "../../../services/newsService";
import dayjs from "dayjs";

const { Text } = Typography;

const POLL_INTERVAL_MS = 15000;

const fmt = (iso) => (iso ? dayjs(iso).format("DD/MM/YYYY HH:mm") : "");
const top5Recent = (items) =>
  [...items].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5);

export default function NewsBanner() {
  const navigate = useNavigate();
  const { idToken } = useUser();

  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  const unsubscribeRef = useRef(null);

  // Initial fetch + WebSocket subscription
  useEffect(() => {
    if (!idToken) return;

    let cancelled = false;

    newsService.obtenerNews(idToken)
      .then((items) => {
        if (!cancelled) {
          setNews(top5Recent(items));
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("[NewsBanner] Error al cargar noticias:", err);
        if (!cancelled) setLoading(false);
      });

    unsubscribeRef.current = newsService.suscribirseANews(
      idToken,
      (newItem) => {
        if (!cancelled) {
          setNews((prev) => top5Recent([newItem, ...prev]));
          setCurrentIndex(0);
        }
      },
      (err) => console.error("[NewsBanner] Error en suscripción:", err)
    );

    return () => {
      cancelled = true;
      unsubscribeRef.current?.();
    };
  }, [idToken]);

  // Polling fallback — refreshes every 15 s in case the subscription misses events
  useEffect(() => {
    if (!idToken) return;
    const interval = setInterval(() => {
      newsService.obtenerNews(idToken)
        .then((items) => setNews(top5Recent(items)))
        .catch(() => {});
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [idToken]);

  // Auto-cycle every 8s
  useEffect(() => {
    if (news.length < 2) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % news.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [news.length]);

  const item = news[currentIndex] ?? null;

  return (
    <div style={bannerStyle}>
      <Space size={8}>
        <BellOutlined style={{ color: METRO_COLORS.primary, fontSize: 14 }} />
        {loading ? (
          <Text type="secondary" style={{ fontSize: 12 }}>Cargando noticias...</Text>
        ) : item ? (
          <>
            <Text strong style={{ fontSize: 13, color: "#2d2d2d" }}>
              {item.name}
            </Text>
            {item.createdAt && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                · {fmt(item.createdAt)}
              </Text>
            )}
          </>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>No hay noticias recientes</Text>
        )}
      </Space>

      <Space size={4}>
        {news.length > 1 && (
          <Space size={4} style={{ marginRight: 8 }}>
            {news.map((_, i) => (
              <div
                key={i}
                onClick={() => setCurrentIndex(i)}
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: i === currentIndex ? METRO_COLORS.primary : "#ccc",
                  cursor: "pointer",
                  transition: "background 0.3s",
                }}
              />
            ))}
          </Space>
        )}
        <Button
          type="link"
          size="small"
          icon={<RightOutlined />}
          onClick={() => navigate("/recordatorios")}
          style={{ color: METRO_COLORS.primary, padding: "0 4px", fontSize: 12 }}
        >
          Ver todas
        </Button>
      </Space>
    </div>
  );
}

const bannerStyle = {
  background: "linear-gradient(90deg, #f0f9e8 0%, #f8fff4 60%, #ffffff 100%)",
  borderBottom: "1px solid #c8e6c9",
  padding: "5px 24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  minHeight: 36,
  zIndex: 999,
};
